import React, { useEffect, useState, useCallback } from "react"

import axios from "axios"
import { detect } from "detect-browser"
import { useStaticQuery, graphql } from "gatsby"
import PropTypes from "prop-types"
import { useGoogleReCaptcha } from "react-google-recaptcha-v3"

import * as styles from "./subscribe-first.module.scss"
import Analytics from "../../../helpers/analytics"
import GrowthChannel from "../../../helpers/growth-channel"
import UrlFunctions from "../../../helpers/url"
import Button from "../../atoms/button"
import CopyContainer from "../../atoms/copy-container"
import FormField from "../../atoms/form-field"
import Heading from "../../atoms/heading"
import Illustration from "../../atoms/illustration"
import Form from "../form"
import FormState from "../form-state"
import SubscribePressList from "../subsribe-press-list"
import Testimonials from "../testimonials"

const SubscribeFirst = ({
  data,
  changeStep,
  changeEmail,
  illustration,
  home,
  all,
  gating,
  light,
}) => {
  const {
    site,
    allSanityDictionary,
    allSanityCountry,
    allSanityCity,
    allSanityConference,
  } = useStaticQuery(graphql`
    {
      site {
        siteMetadata {
          siteUrl
        }
      }
      allSanityDictionary {
        nodes {
          key {
            current
          }
          value
        }
      }
      allSanityCountry {
        nodes {
          abbreviation
          url
        }
      }
      allSanityCity {
        nodes {
          abbreviation
          name
          country {
            abbreviation
            url
          }
        }
      }
      allSanityConference {
        nodes {
          name
        }
      }
    }
  `)

  const [formState, setFormState] = useState("")
  const [email, setEmail] = useState("")
  const [description, setDescription] = useState(data.description._rawDefault)
  const [title, setTitle] = useState(data.heading.default)
  const [conference, setConference] = useState("")
  const [cities, setCities] = useState([])
  const country = allSanityCountry.nodes.find(
    (c) => c.url === site.siteMetadata.siteUrl
  ).abbreviation

  useEffect(() => {
    const descriptionMsg = GrowthChannel(data.description.growth_channel)
    if (descriptionMsg) {
      setDescription(descriptionMsg._rawDescription)
    }

    const titleMsg = GrowthChannel(data.heading.growth_channel)
    if (titleMsg) {
      setTitle(titleMsg.heading)
    }

    const urlConference = UrlFunctions.getParams().conference
    if (
      data.additional_fields?.find((field) => field.type === "conference") &&
      urlConference
    ) {
      const conferenceList = allSanityConference.nodes.filter(
        (c) => c.name === urlConference
      )
      if (conferenceList.length > 0) {
        setConference(urlConference)
      }
    }

    if (UrlFunctions.getParams().city) {
      const citiesList = allSanityCity.nodes.filter(
        (c) =>
          c.abbreviation.toUpperCase() ===
            UrlFunctions.getParams().city.toUpperCase() &&
          c.country.url === data.site?.siteMetadata.siteUrl
      )
      if (citiesList.length > 0) {
        setCities([UrlFunctions.getParams().city.toUpperCase()])
      }
    }
  }, [])

  const { executeRecaptcha } = useGoogleReCaptcha()

  const handleSubmit = useCallback(async (e) => {
    let growthChannel =
      UrlFunctions.getParams().sqf_channel || UrlFunctions.getParams().utm_id
    const adsetName = UrlFunctions.getParams().utm_campaign
    const adName = UrlFunctions.getParams().utm_content
    e.preventDefault()
    setFormState("pending")
    if (!executeRecaptcha) {
      return
    }
    const growthchanneldetails = localStorage.getItem("growthchanneldetails")
      ? localStorage.getItem("growthchanneldetails")
      : UrlFunctions.getParams().growthchanneldetails

    const token = await executeRecaptcha("submit")
    const subscribeId =
      new Date().toISOString() + Math.floor(Math.random() * 10000)

    if (localStorage.getItem("growth_channel"))
      growthChannel = localStorage.getItem("growth_channel")
    const customFields = [
      {
        Key: "GrowthChannel",
        Value: growthChannel,
      },
      { Key: "GrowthChannelDetails", Value: growthchanneldetails },
      { Key: "SourceBrand", Value: UrlFunctions.getParams().sourcebrand },
      { Key: "SubscriptionID", Value: subscribeId },
      {
        Key: "Adset Name",
        Value: adsetName,
      },
      {
        Key: "Ad Name",
        Value: adName,
      },
    ]
    let skipStep = false
    if (data.additional_fields?.length === 0) {
      skipStep = true
    } else if (
      data.additional_fields?.find((field) => field.type === "country")
    ) {
      customFields.push({ Key: "Country", Value: country })
    }
    if (
      conference &&
      data.additional_fields?.find((field) => field.type === "conference")
    ) {
      customFields.push({ Key: "Conference", Value: conference })
      skipStep = true
    } else if (
      cities.length !== 0 &&
      data.additional_fields?.find((field) => field.type === "city")
    ) {
      cities.forEach((city) => {
        customFields.push({ Key: "Cities", Value: city })
      })
      skipStep = true
    }

    const verticalName = data.vertical.slug.current.replace("-", "")

    axios
      .post("/.netlify/functions/createsend", {
        ListId: data.vertical.cm_id,
        token,
        EmailAddress: email,
        CustomFields: customFields,
        Resubscribe: true,
        ConsentToTrack: "Yes",
        browser: detect()?.name,
      })
      .then((res) => {
        Analytics.triggerEvent(
          "subscribe_success",
          {
            vertical: verticalName,
            spam: !!res.data.skip,
            growth_channel: growthChannel,
            growth_channel_details: growthchanneldetails,
            adset_name: adsetName,
            ad_name: adName,
            email,
          },
          "Subscribe",
          subscribeId
        )
        changeStep(
          skipStep || res.data.skip ? "third" : "second",
          data.vertical.slug.current
        )
        sessionStorage.setItem(`subscribed_${verticalName}`, true)
        localStorage.setItem(`subscribed_${verticalName}`, true)
      })
      .catch((error) => {
        Analytics.triggerEvent(
          "subscribe_error",
          {
            vertical: verticalName,
            growth_channel: growthChannel,
            growth_channel_details: growthchanneldetails,
            error_code: error.response.status,
          },
          "SubscribeError"
        )
        setFormState("error")
      })
  })

  const changeEmailStep = (event) => {
    setEmail(event.target.value)
    if (changeEmail) changeEmail(event.target.value)
  }

  const headerLevel = () => {
    if (home || gating) {
      return "3"
    }
    if (all) {
      return "4"
    }
    return "1"
  }

  return (
    <FormState className={styles.formState} condition={formState !== "success"}>
      <Heading level={headerLevel()}>
        {all
          ? `${data.vertical.name} ${
              allSanityDictionary.nodes.find(
                (item) => item.key.current === "newsletter"
              )?.value
            }`
          : title}
        {illustration ? (
          <Illustration
            className={`${!(all || gating) ? styles.showMobile : ""}`}
            size={!gating ? "m" : ""}
            icon={illustration}
          />
        ) : (
          ""
        )}
      </Heading>
      <CopyContainer content={description} />
      <Form
        handleSubmit={handleSubmit}
        errorMessage={
          allSanityDictionary.nodes.find(
            (item) => item.key.current === "error_message"
          )?.value
        }
        submitText={data.ctas.cta}
        formState={formState}
        className={styles.form}
        buttonVariant={home || gating || all ? "secondary" : null}
        trackSubscribe
      >
        <FormField
          label={
            allSanityDictionary.nodes.find(
              (item) => item.key.current === "email"
            )?.value
          }
          form={`subscribe-${data.vertical.slug.current}`}
          field="email"
          type="email"
          value={email}
          changeEvent={changeEmailStep}
          required
          light={light}
          autofocus={!(home || gating || all)}
        />
      </Form>
      {data.quotes?.length > 0 ? (
        <Testimonials
          color={data.vertical.color.replaceAll("-", "")}
          title={data.quotes_title}
          quotes={data.quotes}
          small
        />
      ) : (
        ""
      )}
      {!(home || gating || all) && data.ctas.cta_supplemental ? (
        <Button variant="secondary" url={data.ctas.cta_supplemental.url}>
          {data.ctas.cta_supplemental.text}
        </Button>
      ) : (
        ""
      )}

      {!all ? (
        <SubscribePressList
          press={data.press}
          label={data.press_title}
          light={light}
        />
      ) : (
        ""
      )}
    </FormState>
  )
}

export default SubscribeFirst

SubscribeFirst.propTypes = {
  data: PropTypes.object.isRequired,
  changeStep: PropTypes.func.isRequired,
  changeEmail: PropTypes.func.isRequired,
  illustration: PropTypes.string,
  home: PropTypes.bool,
  all: PropTypes.bool,
  light: PropTypes.bool,
  gating: PropTypes.bool,
}

SubscribeFirst.defaultProps = {
  home: false,
  all: false,
  gating: false,
  light: true,
  illustration: null,
}
